import React, { useState } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { isAdmin } from '~/hooks/permissions';

// Actions
import {
  getAllTransactions,
  disableAutoFunding,
  enableAutoFunding,
} from '~/redux/actions/stripe-actions';
import { getUserData } from '~/redux/actions/auth-actions';
import { success } from '~/redux/actions/snackbar-actions';

// Components
import AddFunds from '../AddFunds';
import ConfirmModal from '~/components/General/ConfirmModal';
import EnableFunding from '../EnableFunding';
import OlcToggle from '~/components/General/OlcToggle';

// Utils
import { balanceFormatter } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';
import { ROLES } from '~/utils/constants';

// MUI Components
import { Box, Typography, Tooltip, Skeleton } from '@mui/material';
import IconButton from '@mui/material/IconButton';

// Icons
import { WalletIcon } from '~/assets/images';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const BalanceBoard = ({ openPaymentMethodModal }) => {
  const dispatch = useDispatch();
  const [fundsModal, setFundsModal] = useState(false);
  const [autoFundingModal, setAutoFundingModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    type: '',
    icon: null,
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    isConfirmDialog: false,
  });
  const user = useSelector((state) => state.userReducers?.user);
  const canAdmin = isAdmin();
  const isAutoFundingEnabled = user?.organization?.isAutoFundingEnabled !== false;
  const minAutoFundingAmount = user?.organization?.minAutoFundingAmount || 50;

  const handleCloseDialog = () => {
    setOpenConfirmModal({
      open: false,
      message: '',
      header: '',
      loading: false,
      handleOk: () => {},
      handleClose: () => {},
      type: '',
      icon: null,
      successButtonName: 'Yes',
      cancelButtonName: 'No',
      isConfirmDialog: false,
    });
  };

  const isUser = !(user?.role?.slug === ROLES['ORGANIZATION_ADMIN']);

  const handleDisable = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    const data = await disableAutoFunding();
    if (data.status === 200) {
      dispatch(getUserData());
      dispatch(
        success(
          MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING_DISABLED_SUCCESS,
          MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING
        )
      );
      setOpenConfirmModal({
        open: false,
        message: '',
        header: '',
        loading: false,
        handleOk: () => {},
        handleClose: () => {},
        type: '',
      });
    }
    setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
  };

  const handleEnable = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    const data = await enableAutoFunding();
    if (data.status === 200) {
      dispatch(getUserData());
      dispatch(
        success(
          MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING_ENABLED_SUCCESS,
          MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING
        )
      );
      setOpenConfirmModal({
        open: false,
        message: '',
        header: '',
        loading: false,
        handleOk: () => {},
        handleClose: () => {},
        type: '',
      });
    }
    setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
  };

  const handleAutoFundingToggleClick = () => {
    if (isUser) return;
    const isDisabling = isAutoFundingEnabled;

    setOpenConfirmModal({
      open: true,
      message: `Are you sure you want to ${
        isDisabling ? 'disable' : 'enable'
      } auto funding for orders?`,
      header: isDisabling
        ? MESSAGES.ACCOUNT.WALLET.STRIPE.DISABLE_AUTO_FUNDING
        : MESSAGES.ACCOUNT.WALLET.STRIPE.ENABLE_AUTO_FUNDING,
      loading: false,
      handleOk: isDisabling ? handleDisable : handleEnable,
      handleClose: handleCloseDialog,
      type: isDisabling ? 'disable' : 'enable',
      successButtonName: 'Yes',
      cancelButtonName: 'No',
      icon: WalletIcon,
    });
  };

  return (
    <>
      <Box className="internalBox PaymentMethodsSection">
        <Box className="internalBoxHeader">
          <Box>
            <AccountBalanceWalletOutlinedIcon className="walletCardHeaderIcon" />
          </Box>
          <Box>
            <Typography className="internalBoxHeaderTitle">
              {MESSAGES.ACCOUNT.WALLET.STRIPE.YOUR_BALANCE}
            </Typography>
          </Box>
          <Box className="headerActionBtn">
            {user?.organization == undefined ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={40}
                sx={{ borderRadius: '8px' }}
              />
            ) : (
              <Tooltip title={isUser ? MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_ADD_FUND : ''}>
                <OlcButton
                  variant="primary"
                  className="olcPrimaryBtnPayment"
                  onClick={() => {
                    if (isUser) {
                      return null;
                    }

                    setFundsModal(true);
                  }}
                  disabled={isUser}
                >
                  <AddIcon /> Add Funds
                </OlcButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box>
          <Typography className="balanceAmount">
            {user?.organization?.balance == undefined ? (
              <Skeleton variant="text" width={140} height={28} />
            ) : (
              balanceFormatter(user?.organization?.balance)
            )}
          </Typography>
        </Box>

        <Box sx={{ marginTop: 'auto' }}>
          {user?.organization == undefined ? (
            <Box className="subTextSectionWrapper" sx={{ marginBottom: '20px' }}>
              <Box className="subTextSection">
                <Skeleton variant="text" width={260} height={20} />
              </Box>
              <Box className="subTextSection" sx={{ marginTop: '8px' }}>
                <Skeleton variant="text" width={260} height={20} />
              </Box>
            </Box>
          ) : (
            <Box className="subTextSectionWrapper" sx={{ marginBottom: '20px' }}>
              <Box className="subTextSection autoFundingStatusRow">
                <Box className="subTextItem1">
                  <Typography className="subTextItem1">
                    {MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING}:
                    <span className="subTextItem2">
                      {isAutoFundingEnabled
                        ? MESSAGES.ACCOUNT.WALLET.STRIPE.ENABLED
                        : MESSAGES.ACCOUNT.WALLET.STRIPE.DISABLED}
                    </span>
                  </Typography>
                </Box>
                <Tooltip title={isUser ? MESSAGES.ACCOUNT.WALLET.STRIPE.UNAUTHORIZED_ADD_FUND : ''}>
                  <OlcToggle
                    checked={isAutoFundingEnabled}
                    disabled={isUser}
                    onChange={handleAutoFundingToggleClick}
                    className="autoFundingToggleWrapper"
                    ariaLabel={`${MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING} toggle`}
                  />
                </Tooltip>
              </Box>

              <Box className="subTextSection">
                <Box className="subTextItem1 minAutoFundingRow">
                  <Typography className="subTextItem1 minAutoFundingText">
                    {MESSAGES.ACCOUNT.WALLET.STRIPE.MIN_AUTO_FUNDING}{' '}
                    <span className="subTextItem2">
                      {canAdmin && minAutoFundingAmount
                        ? balanceFormatter(minAutoFundingAmount)
                        : 'N/A'}
                    </span>
                  </Typography>
                  {canAdmin && (
                    <Tooltip title={MESSAGES.ACCOUNT.WALLET.STRIPE.UPDATE_AUTO_FUNDING}>
                      <IconButton
                        aria-label={MESSAGES.ACCOUNT.WALLET.STRIPE.UPDATE_AUTO_FUNDING}
                        className="minAutoFundingEditBtn"
                        size="small"
                        onClick={() => setAutoFundingModal(true)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {fundsModal && (
        <AddFunds
          open={fundsModal}
          handleOpen={() => {
            setFundsModal(true);
          }}
          handleClose={() => {
            setFundsModal(false);
          }}
          openPaymentMethodModal={() => {
            openPaymentMethodModal();
          }}
          onSuccessCallback={() => {
            dispatch(getUserData());
            dispatch(
              success(
                MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_FUNDS_SUCCESS,
                MESSAGES.ACCOUNT.WALLET.STRIPE.FUNDS_ADDED
              )
            );
            dispatch(getAllTransactions(1, 20));
          }}
        />
      )}
      {autoFundingModal && (
        <EnableFunding
          open={autoFundingModal}
          handleClose={() => {
            setAutoFundingModal(false);
          }}
        />
      )}
      {openConfirmModal.open && (
        <ConfirmModal
          openConfirmModal={{
            open: openConfirmModal.open,
            message: openConfirmModal.message,
            header: openConfirmModal.header,
            icon: openConfirmModal.icon,
            loading: openConfirmModal.loading,
            successButtonName: openConfirmModal.successButtonName,
            cancelButtonName: openConfirmModal.cancelButtonName,
            handleOk: openConfirmModal.handleOk,
            handleClose: openConfirmModal.handleClose,
          }}
        />
      )}
    </>
  );
};

export default BalanceBoard;
